import { type JSX, type MouseEvent, useState } from "react"
import { createRoot } from "react-dom/client"
import type { PictureRecord } from "./pictures/api"
import { AssetManagerModal } from "./pictures/asset_manager_modal"

type EmailVerificationProps = {
  resendUrl: string
}

function EmailVerificationStatus({ resendUrl }: EmailVerificationProps): JSX.Element {
  const [hidden, setHidden] = useState(false)

  function handleClick(_event: MouseEvent<HTMLAnchorElement>): void {
    setHidden(true)
  }

  if (hidden) return <></>

  return (
    <>
      {" "}
      <a
        href={resendUrl}
        className="settings__verification-link"
        onClick={handleClick}
      >
        Resend verification email?
      </a>
    </>
  )
}

// The avatar chooser: the picture itself opens the asset manager, keep_or_mull
// style. It's a real <button> rather than a click handler on the image, so it
// is reachable by keyboard and announces itself; the hint beside it is what
// tells a first-time visitor the picture is clickable at all.
//
// Picking only stages the choice — nothing is saved until "Save changes", so
// the preview and the hidden id move together and the server sees one id.
function AvatarPicker({
  picturesUrl,
  initialUrl,
  fallbackUrl,
}: {
  picturesUrl: string
  initialUrl: string | null
  fallbackUrl: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [pictureId, setPictureId] = useState("")
  const [previewUrl, setPreviewUrl] = useState(initialUrl)

  function choose(picture: PictureRecord): void {
    setPictureId(picture.id)
    setPreviewUrl(picture.url)
  }

  return (
    <>
      <button
        type="button"
        className="settings__avatar-button"
        aria-label="Change your profile picture"
        onClick={() => setOpen(true)}
      >
        <img className="settings__avatar-img" src={previewUrl ?? fallbackUrl} alt="" />
        <span className="settings__avatar-overlay" aria-hidden="true">Change</span>
      </button>
      <div className="settings__avatar-controls">
        <input type="hidden" name="user[avatar_picture_id]" value={pictureId} />
        <p className="settings__hint">
          {pictureId
            ? "New picture selected, save your changes to keep it."
            : ""}
        </p>
      </div>
      <AssetManagerModal
        picturesUrl={picturesUrl}
        open={open}
        onClose={() => setOpen(false)}
        onAttach={choose}
        title="Choose a Profile Picture"
        confirmLabel="Use This Picture"
      />
    </>
  )
}

export function mountSettingsApps(): void {
  document.querySelectorAll<HTMLElement>("[data-settings-email-verification-app]").forEach((el) => {
    const resendUrl = el.dataset.resendUrl ?? ""
    if (!resendUrl) return

    createRoot(el).render(<EmailVerificationStatus resendUrl={resendUrl} />)
  })

  document.querySelectorAll<HTMLElement>("[data-settings-avatar-app]").forEach((el) => {
    const picturesUrl = el.dataset.picturesUrl ?? ""
    const fallbackUrl = el.dataset.fallbackUrl ?? ""
    if (!picturesUrl) return

    createRoot(el).render(
      <AvatarPicker
        picturesUrl={picturesUrl}
        initialUrl={el.dataset.avatarUrl || null}
        fallbackUrl={fallbackUrl}
      />,
    )
  })
}

